import { useState, FormEvent, useRef } from "react";

interface InstallInquiryModalProps {
  onClose: () => void;
}

const REPLY_OPTIONS = [
  { value: "phone", label: "전화로 회신", icon: "ri-phone-line" },
  { value: "email", label: "이메일로 회신", icon: "ri-mail-line" },
  { value: "both", label: "둘 다 괜찮아요", icon: "ri-customer-service-2-line" },
];

const InstallInquiryModal = ({ onClose }: InstallInquiryModalProps) => {
  const [replyMethod, setReplyMethod] = useState("phone");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const textarea = form.querySelector("textarea");
    if (textarea && textarea.value.length > 500) return;
    setLoading(true);
    const formData = new FormData(form);
    formData.set("reply_method", replyMethod === "phone" ? "전화로 회신" : replyMethod === "email" ? "이메일로 회신" : "전화 + 이메일 모두");
    const data = new URLSearchParams(formData as unknown as Record<string, string>);
    await fetch("https://readdy.ai/api/form/d7do4dk5q56ra7tufnr0", { method: "POST", body: data });
    setLoading(false);
    setSubmitted(true);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-lg shadow-xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-stone-100">
          <div>
            <p className="text-xs font-bold tracking-widest uppercase text-stone-400 mb-0.5">Installation</p>
            <h2 className="text-xl font-black text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>설치 문의하기</h2>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-stone-100 transition-colors cursor-pointer">
            <i className="ri-close-line text-stone-500 text-lg" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          {submitted ? (
            <div className="flex flex-col items-center justify-center py-10">
              <div className="w-14 h-14 flex items-center justify-center bg-emerald-100 rounded-full mb-4">
                <i className="ri-check-line text-2xl text-emerald-600" />
              </div>
              <h3 className="text-lg font-bold text-stone-900 mb-2">문의가 접수되었습니다!</h3>
              <p className="text-sm text-stone-500 text-center leading-relaxed">
                선택하신 방법으로 빠르게 연락드리겠습니다.<br />
                <span className="font-semibold text-stone-700">010-9793-9490</span>으로 바로 전화하셔도 됩니다.
              </p>
              <button onClick={onClose} className="mt-6 bg-stone-900 text-white text-sm font-bold px-8 py-2.5 rounded-full hover:bg-stone-700 transition-colors cursor-pointer whitespace-nowrap">
                닫기
              </button>
            </div>
          ) : (
            <form ref={formRef} data-readdy-form onSubmit={handleSubmit} className="space-y-4">
              {/* Name + Phone */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-stone-700 mb-1.5">이름 *</label>
                  <input
                    name="name"
                    required
                    className="w-full border border-stone-200 rounded-lg px-3.5 py-2.5 text-sm text-stone-800 outline-none focus:border-stone-400 transition-colors"
                    placeholder="홍길동"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-stone-700 mb-1.5">전화번호 *</label>
                  <input
                    name="phone"
                    required
                    type="tel"
                    className="w-full border border-stone-200 rounded-lg px-3.5 py-2.5 text-sm text-stone-800 outline-none focus:border-stone-400 transition-colors"
                    placeholder="010-0000-0000"
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block text-xs font-semibold text-stone-700 mb-1.5">이메일</label>
                <input
                  name="email"
                  type="email"
                  className="w-full border border-stone-200 rounded-lg px-3.5 py-2.5 text-sm text-stone-800 outline-none focus:border-stone-400 transition-colors"
                  placeholder="example@email.com"
                />
              </div>

              {/* Address */}
              <div>
                <label className="block text-xs font-semibold text-stone-700 mb-1.5">설치 주소 *</label>
                <input
                  name="address"
                  required
                  className="w-full border border-stone-200 rounded-lg px-3.5 py-2.5 text-sm text-stone-800 outline-none focus:border-stone-400 transition-colors"
                  placeholder="예) 경기도 의정부시 ..."
                />
              </div>

              {/* Message */}
              <div>
                <label className="block text-xs font-semibold text-stone-700 mb-1.5">
                  문의 내용 <span className="text-stone-400 font-normal">(최대 500자)</span>
                </label>
                <textarea
                  name="message"
                  rows={3}
                  maxLength={500}
                  onChange={(e) => setCharCount(e.target.value.length)}
                  className="w-full border border-stone-200 rounded-lg px-3.5 py-2.5 text-sm text-stone-800 outline-none focus:border-stone-400 transition-colors resize-none"
                  placeholder="설치 희망 제품, 현관 구조, 기타 요청사항 등을 자유롭게 적어주세요."
                />
                <p className="text-right text-xs text-stone-400 mt-1">{charCount}/500</p>
              </div>

              {/* Reply Method */}
              <div>
                <label className="block text-xs font-semibold text-stone-700 mb-2">회신 방법 선택 *</label>
                <div className="grid grid-cols-3 gap-2">
                  {REPLY_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setReplyMethod(opt.value)}
                      className={`flex flex-col items-center gap-1.5 py-3 rounded-xl border text-xs font-semibold transition-all cursor-pointer ${
                        replyMethod === opt.value
                          ? "border-stone-900 bg-stone-900 text-white"
                          : "border-stone-200 text-stone-600 hover:border-stone-400"
                      }`}
                    >
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className={`${opt.icon} text-base`} />
                      </div>
                      <span className="whitespace-nowrap">{opt.label}</span>
                    </button>
                  ))}
                </div>
                {(replyMethod === "email" || replyMethod === "both") && (
                  <p className="text-xs text-stone-400 mt-2">
                    <i className="ri-information-line mr-1" />
                    이메일 회신을 원하시면 위에 이메일 주소를 꼭 입력해주세요.
                  </p>
                )}
              </div>

              {/* Direct call hint */}
              <div className="flex items-center gap-2 bg-stone-50 rounded-xl px-4 py-3">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-phone-fill text-stone-500 text-sm" />
                </div>
                <p className="text-xs text-stone-500">
                  바로 통화 원하시면{" "}
                  <a href="tel:01097939490" className="font-bold text-stone-800 underline underline-offset-2 cursor-pointer">
                    010-9793-9490
                  </a>
                  으로 전화주세요.
                </p>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-stone-900 text-white text-sm font-bold py-3.5 rounded-xl hover:bg-stone-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60"
              >
                {loading ? "전송 중..." : "문의 접수하기"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default InstallInquiryModal;
